import { initialize } from "launchdarkly-js-client-sdk";
import type { AppEnvironmentValue } from "./env.ts";
import { AllFeatureFlags } from "@packages/core/feature-flags/featureFlags.ts";

const LOCAL_STORAGE_FLAGS_KEY = "msw-devtools-feature-flags";

function getDefaultFlags(): Record<string, unknown> {
    return Object.fromEntries(Object.entries(AllFeatureFlags).map(([x, y]) => [x, y.defaultValue]));
}

async function createLocalStorageClient(flags: Record<string, unknown>) {
    const { createLocalStorageLaunchDarklyClient } = await import("@squide/firefly");

    return createLocalStorageLaunchDarklyClient(flags, {
        localStorageKey: LOCAL_STORAGE_FLAGS_KEY
    });
}

function initializeLaunchDarklyForMsw() {
    return createLocalStorageClient(getDefaultFlags());
}

async function initializeLaunchDarklyForDemo() {
    const { getDemoDefaultFlags, mergeDemoFlags, readDemoFlagOverrides } = await import("@packages/devtools/client/demo/demoFeatureFlags.ts");

    localStorage.removeItem(LOCAL_STORAGE_FLAGS_KEY);

    return createLocalStorageClient(mergeDemoFlags(getDemoDefaultFlags(), readDemoFlagOverrides()));
}

async function initializeLaunchDarklyForLocal(apiBaseUrl: string) {
    const { initializeApiLaunchDarklyClient } = await import("@packages/devtools/client/feature-flags/initializeApiLaunchDarklyClient.ts");

    return initializeApiLaunchDarklyClient(apiBaseUrl);
}

async function initializeLaunchDarkly(clientId?: string) {
    if (!clientId) {
        throw new Error("[web-app] A LaunchDarkly client id is required to initialize the LaunchDarkly client. Did you set a PUBLIC_LAUNCH_DARKLY_CLIENT_ID environment variable?");
    }

    const client = initialize(
        clientId,
        {
            kind: "user",
            anonymous: true
        },
        {
            streaming: true
        }
    );

    try {
        await client.waitForInitialization(5);

        console.info("[web-app] LaunchDarly client is initialized with the following flags:", client.allFlags());
    } catch (error: unknown) {
        console.error("[web-app] LaunchDarkly client failed to initialize:", error);
    }

    return client;
}

export function getLaunchDarklyInitializer(appEnv: AppEnvironmentValue, isDemo: boolean, apiBaseUrl: string, useRemoteFlagsOverride: boolean, launchDarklyClientId?: string) {
    if (isDemo) {
        return initializeLaunchDarklyForDemo;
    }

    if (!useRemoteFlagsOverride) {
        if (appEnv === "msw") {
            return initializeLaunchDarklyForMsw;
        }

        if (appEnv === "local") {
            return () => initializeLaunchDarklyForLocal(apiBaseUrl);
        }
    }

    return () => initializeLaunchDarkly(launchDarklyClientId);
}
